import type { AIUseCase, PainPoint, FeasibilityReview } from "./types"

export const useCases: AIUseCase[] = [
  {
    id: "AI-UC-001",
    name: "Regulatory Circular Summarization Assistant",
    department: "Compliance",
    businessOwner: "Head of Compliance Operations",
    aiOwner: "AI Governance Team",
    businessProblem:
      "Regulatory circulars are reviewed manually, causing delays and risk of missed obligations.",
    currentWorkflow:
      "Compliance officers download circulars, read them manually, identify obligations, and map them to policies.",
    candidateSolution:
      "An AI assistant that summarizes regulatory circulars, extracts obligations, maps them to internal policies, and creates action items for compliance teams.",
    expectedBenefit: "60% reduction in review time and improved obligation tracking.",
    expectedTimeSaving: "50–60%",
    expectedCostSaving: "Medium",
    expectedRiskReduction: "High",
    dataClassification: "L0",
    feasibilityFlag: "Green",
    dependencies: ["Document repository", "Policy database", "Maker-checker workflow"],
    priorityScore: 86,
    priorityBand: "P1",
    status: "Approved for Discovery",
    createdAt: "2026-07-01",
    updatedAt: "2026-07-08",
    businessImpact: 5,
    riskComplianceValue: 5,
    feasibilityScore: 4,
    urgency: 4,
    reusability: 4,
    dataReadiness: 5,
  },
  {
    id: "AI-UC-002",
    name: "Customer Complaint Classification",
    department: "Customer Support",
    businessOwner: "Head of Contact Center",
    aiOwner: "AI Solutions Team",
    businessProblem:
      "Customer complaints are manually categorized before routing to relevant teams.",
    currentWorkflow:
      "Agents read complaints, select categories manually, and assign cases to departments.",
    candidateSolution:
      "An NLP-based classifier that automatically categorizes incoming complaints and routes them to the appropriate team.",
    expectedBenefit: "40% faster complaint routing and improved SLA compliance.",
    expectedTimeSaving: "40%",
    expectedCostSaving: "Medium",
    expectedRiskReduction: "Medium",
    dataClassification: "L1",
    feasibilityFlag: "Amber",
    dependencies: ["CRM integration", "PII masking", "Case management API"],
    priorityScore: 74,
    priorityBand: "P2",
    status: "Feasibility Pending",
    createdAt: "2026-07-02",
    updatedAt: "2026-07-06",
    businessImpact: 4,
    riskComplianceValue: 3,
    feasibilityScore: 3,
    urgency: 4,
    reusability: 4,
    dataReadiness: 3,
  },
  {
    id: "AI-UC-003",
    name: "Internal Policy Q&A Assistant",
    department: "HR",
    businessOwner: "HR Operations Manager",
    aiOwner: "AI Governance Team",
    businessProblem:
      "Employees ask repetitive HR policy questions, increasing manual workload on HR staff.",
    currentWorkflow: "Employees email HR or search policy PDFs manually.",
    candidateSolution:
      "A RAG-based Q&A assistant trained on internal HR policy documents enabling employee self-service.",
    expectedBenefit: "Reduced HR queries and faster employee self-service.",
    expectedTimeSaving: "45%",
    expectedCostSaving: "Low",
    expectedRiskReduction: "Low",
    dataClassification: "L0",
    feasibilityFlag: "Green",
    dependencies: ["HR policy documents", "Internal knowledge base"],
    priorityScore: 81,
    priorityBand: "P1",
    status: "Prioritized",
    createdAt: "2026-07-03",
    updatedAt: "2026-07-07",
    businessImpact: 4,
    riskComplianceValue: 3,
    feasibilityScore: 5,
    urgency: 4,
    reusability: 5,
    dataReadiness: 4,
  },
  {
    id: "AI-UC-004",
    name: "Transaction Anomaly Explanation Assistant",
    department: "Fraud Risk",
    businessOwner: "Fraud Monitoring Lead",
    aiOwner: "AI Risk Analytics Team",
    businessProblem:
      "Analysts spend significant time understanding why transactions are flagged.",
    currentWorkflow:
      "Analysts review transaction patterns, customer history, device data, and rule hits manually.",
    candidateSolution:
      "An LLM-based assistant that generates natural language explanations for flagged transactions using fraud system data.",
    expectedBenefit: "Faster alert triage and improved fraud investigation quality.",
    expectedTimeSaving: "35%",
    expectedCostSaving: "High",
    expectedRiskReduction: "High",
    dataClassification: "L1",
    feasibilityFlag: "Amber",
    dependencies: ["Fraud system integration", "Transaction data access", "Secure LLM environment"],
    priorityScore: 78,
    priorityBand: "P2",
    status: "AI Mapped",
    createdAt: "2026-07-04",
    updatedAt: "2026-07-08",
    businessImpact: 4,
    riskComplianceValue: 5,
    feasibilityScore: 3,
    urgency: 4,
    reusability: 3,
    dataReadiness: 3,
  },
  {
    id: "AI-UC-005",
    name: "IT Ticket Auto-Triage Assistant",
    department: "IT Operations",
    businessOwner: "IT Service Desk Manager",
    aiOwner: "AI Solutions Team",
    businessProblem: "IT tickets are manually reviewed and assigned, delaying resolution.",
    currentWorkflow:
      "Service desk reviews each ticket, identifies issue category, priority, and assignment group.",
    candidateSolution:
      "An AI classifier that reads incoming IT tickets and auto-assigns them to the correct team with a suggested priority.",
    expectedBenefit: "30% faster ticket assignment and better SLA management.",
    expectedTimeSaving: "30%",
    expectedCostSaving: "Medium",
    expectedRiskReduction: "Low",
    dataClassification: "L0",
    feasibilityFlag: "Green",
    dependencies: ["ITSM API", "Ticket history"],
    priorityScore: 69,
    priorityBand: "P2",
    status: "Feasibility Completed",
    createdAt: "2026-07-05",
    updatedAt: "2026-07-09",
    businessImpact: 3,
    riskComplianceValue: 2,
    feasibilityScore: 4,
    urgency: 4,
    reusability: 4,
    dataReadiness: 4,
  },
]

export const painPoints: PainPoint[] = [
  {
    id: "PP-001",
    department: "Compliance",
    businessOwner: "Head of Compliance Operations",
    processName: "Regulatory Circular Review",
    businessProblem: "Regulatory circulars are reviewed manually, causing delays and risk of missed obligations.",
    currentWorkflow: "Compliance officers download circulars, read them manually, identify obligations, and map them to policies.",
    frequency: "Daily",
    estimatedTimeSpent: "3 hours per circular",
    monthlyVolume: 25,
    systemsInvolved: ["Email", "Document Repository", "Policy Management System"],
    dataInvolved: "Regulatory circulars and internal policies",
    expectedBenefit: "Reduce manual review time by 60% and improve obligation tracking accuracy.",
    urgency: "High",
    status: "Mapped",
    createdAt: "2026-07-01",
  },
  {
    id: "PP-002",
    department: "Customer Support",
    businessOwner: "Head of Contact Center",
    processName: "Customer Complaint Categorization",
    businessProblem: "Agents manually categorize complaints before routing, causing delays.",
    currentWorkflow: "Agents read complaints, select categories manually, and assign cases to departments.",
    frequency: "Continuous",
    estimatedTimeSpent: "5 minutes per complaint",
    monthlyVolume: 5000,
    systemsInvolved: ["CRM", "Case Management System", "Email"],
    dataInvolved: "Customer complaint text (contains PII)",
    expectedBenefit: "40% faster routing and improved SLA adherence.",
    urgency: "High",
    status: "Mapped",
    createdAt: "2026-07-02",
  },
  {
    id: "PP-003",
    department: "Finance",
    businessOwner: "Finance Operations Manager",
    processName: "Invoice Processing and Reconciliation",
    businessProblem: "Finance team manually processes hundreds of vendor invoices each month.",
    currentWorkflow: "Staff download invoices, extract data, match with POs, and enter into ERP system.",
    frequency: "Monthly",
    estimatedTimeSpent: "8 hours per batch",
    monthlyVolume: 300,
    systemsInvolved: ["ERP", "Email", "Shared Drive"],
    dataInvolved: "Vendor invoices, purchase orders, financial data",
    expectedBenefit: "70% reduction in manual data entry and faster month-end close.",
    urgency: "Medium",
    status: "Submitted",
    createdAt: "2026-07-05",
  },
  {
    id: "PP-004",
    department: "Risk Management",
    businessOwner: "Chief Risk Officer",
    processName: "Risk Report Generation",
    businessProblem: "Risk reports are manually compiled from multiple data sources each week.",
    currentWorkflow: "Risk analysts pull data from multiple systems, consolidate in Excel, and draft narratives.",
    frequency: "Weekly",
    estimatedTimeSpent: "12 hours per report",
    monthlyVolume: 4,
    systemsInvolved: ["Risk System", "Data Warehouse", "Excel", "PowerPoint"],
    dataInvolved: "Risk metrics, exposure data, internal reports",
    expectedBenefit: "80% reduction in report preparation time and improved accuracy.",
    urgency: "Medium",
    status: "Draft",
    createdAt: "2026-07-07",
  },
]

export const feasibilityReviews: FeasibilityReview[] = [
  {
    id: "FR-001",
    useCaseId: "AI-UC-001",
    reviewTeam: "SOA",
    reviewStatus: "Feasible",
    feasibilityRating: 5,
    dependencies: ["Document repository API", "Policy database connector"],
    blockers: [],
    comments: "Architecture fit is acceptable. APIs for document access are available. Integration with existing document repository is straightforward.",
    reviewedBy: "SOA Reviewer",
    reviewDate: "2026-07-09",
  },
  {
    id: "FR-002",
    useCaseId: "AI-UC-001",
    reviewTeam: "Infra",
    reviewStatus: "Feasible with Conditions",
    feasibilityRating: 4,
    dependencies: ["Internal VM cluster", "Container hosting environment"],
    blockers: [],
    comments: "Requires internal container deployment. GPU not needed for summarization tasks. Storage requirements are manageable.",
    reviewedBy: "Infra Reviewer",
    reviewDate: "2026-07-09",
  },
  {
    id: "FR-003",
    useCaseId: "AI-UC-001",
    reviewTeam: "Network",
    reviewStatus: "Feasible",
    feasibilityRating: 5,
    dependencies: ["Internal network routing"],
    blockers: [],
    comments: "No external API calls required. All components can operate within the internal network perimeter.",
    reviewedBy: "Network Reviewer",
    reviewDate: "2026-07-10",
  },
  {
    id: "FR-004",
    useCaseId: "AI-UC-001",
    reviewTeam: "IT-Ops",
    reviewStatus: "Feasible with Conditions",
    feasibilityRating: 4,
    dependencies: ["Monitoring setup", "Logging infrastructure"],
    blockers: [],
    comments: "Production readiness requires setting up dedicated monitoring dashboards and incident response playbook.",
    reviewedBy: "IT-Ops Reviewer",
    reviewDate: "2026-07-10",
  },
  {
    id: "FR-005",
    useCaseId: "AI-UC-002",
    reviewTeam: "SOA",
    reviewStatus: "Feasible with Conditions",
    feasibilityRating: 3,
    dependencies: ["CRM API integration", "PII masking service"],
    blockers: ["PII masking service not yet available"],
    comments: "CRM integration is feasible but PII masking must be implemented before processing customer data.",
    reviewedBy: "SOA Reviewer",
    reviewDate: "2026-07-08",
  },
  {
    id: "FR-006",
    useCaseId: "AI-UC-002",
    reviewTeam: "Infra",
    reviewStatus: "More Info Required",
    feasibilityRating: 3,
    dependencies: ["Model hosting environment"],
    blockers: ["Compute capacity not confirmed"],
    comments: "Need to confirm available GPU/CPU capacity for real-time inference at the expected complaint volume.",
    reviewedBy: "Infra Reviewer",
    reviewDate: "2026-07-08",
  },
  {
    id: "FR-007",
    useCaseId: "AI-UC-005",
    reviewTeam: "SOA",
    reviewStatus: "Feasible",
    feasibilityRating: 5,
    dependencies: ["ITSM API"],
    blockers: [],
    comments: "ITSM API is well-documented and accessible. Integration is straightforward.",
    reviewedBy: "SOA Reviewer",
    reviewDate: "2026-07-08",
  },
  {
    id: "FR-008",
    useCaseId: "AI-UC-005",
    reviewTeam: "Infra",
    reviewStatus: "Feasible",
    feasibilityRating: 5,
    dependencies: [],
    blockers: [],
    comments: "Lightweight classification model. Existing infrastructure is sufficient.",
    reviewedBy: "Infra Reviewer",
    reviewDate: "2026-07-08",
  },
  {
    id: "FR-009",
    useCaseId: "AI-UC-005",
    reviewTeam: "Network",
    reviewStatus: "Feasible",
    feasibilityRating: 5,
    dependencies: [],
    blockers: [],
    comments: "Internal only. No special network configuration required.",
    reviewedBy: "Network Reviewer",
    reviewDate: "2026-07-09",
  },
  {
    id: "FR-010",
    useCaseId: "AI-UC-005",
    reviewTeam: "IT-Ops",
    reviewStatus: "Feasible",
    feasibilityRating: 4,
    dependencies: ["Monitoring alerts"],
    blockers: [],
    comments: "Standard monitoring and alerting setup is sufficient.",
    reviewedBy: "IT-Ops Reviewer",
    reviewDate: "2026-07-09",
  },
]
